import { LocalDate } from "@js-joda/core";
import type {
  CreateSalaryPayoutFileRequest,
  SalaryPayoutPreviewResponse,
} from "model/Salary";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { getBackendErrorMessageFromUnknown } from "services/api/backendError";
import { useAlertStore } from "stores/alertStore/alertStore";
import { resolveSalaryPayoutCreateErrorMessage } from "./salaryPayoutPanel.logic";

export type SalaryPayoutPreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; data: SalaryPayoutPreviewResponse };

interface UseSalaryPayoutPanelParams {
  companyId: number | undefined;
  currentDate: LocalDate;
}

interface UseSalaryPayoutPanelReturn {
  canCreatePayoutFile: boolean;
  closePanel: () => void;
  createPayoutFile: () => Promise<void>;
  excludedWorkerCount: number;
  isCreatingPayoutFile: boolean;
  isOpen: boolean;
  openPanel: () => void;
  payoutDate: LocalDate | null;
  payoutWorkerCount: number;
  previewState: SalaryPayoutPreviewState;
  setPayoutDate: (date: LocalDate | null) => void;
  totalNetPayoutAmount: number;
}

const getDefaultPayoutDate = (currentDate: LocalDate): LocalDate => {
  const defaultDay = Math.min(25, currentDate.lengthOfMonth());
  return LocalDate.of(currentDate.year(), currentDate.monthValue(), defaultDay);
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const uniqueByWorkerId = <TWorker extends { workerId: number }>(
  workers: readonly TWorker[],
): TWorker[] => {
  const uniqueWorkers = new Map<number, TWorker>();
  workers.forEach((worker) => {
    if (uniqueWorkers.has(worker.workerId)) return;
    uniqueWorkers.set(worker.workerId, worker);
  });
  return Array.from(uniqueWorkers.values());
};

const normalizeSalaryPayoutPreview = (
  preview: SalaryPayoutPreviewResponse,
): SalaryPayoutPreviewResponse => {
  const payoutWorkers = uniqueByWorkerId(preview.payoutWorkers);
  const payoutWorkerIds = new Set(
    payoutWorkers.map((worker) => worker.workerId),
  );

  const noPayoutWorkers = uniqueByWorkerId(preview.noPayoutWorkers).filter(
    (worker) => !payoutWorkerIds.has(worker.workerId),
  );
  const noPayoutWorkerIds = new Set(
    noPayoutWorkers.map((worker) => worker.workerId),
  );

  const noPaymentMethodWorkers = uniqueByWorkerId(
    preview.noPaymentMethodWorkers,
  ).filter((worker) => {
    return (
      !payoutWorkerIds.has(worker.workerId) &&
      !noPayoutWorkerIds.has(worker.workerId)
    );
  });

  return {
    noPaymentMethodWorkers,
    noPayoutWorkers,
    payoutWorkers,
  };
};

export const useSalaryPayoutPanel = ({
  companyId,
  currentDate,
}: UseSalaryPayoutPanelParams): UseSalaryPayoutPanelReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewState, setPreviewState] = useState<SalaryPayoutPreviewState>({
    status: "idle",
  });
  const [payoutDate, setPayoutDate] = useState<LocalDate | null>(
    getDefaultPayoutDate(currentDate),
  );
  const [isCreatingPayoutFile, setIsCreatingPayoutFile] = useState(false);
  const isCreatingPayoutFileRef = useRef(false);
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();

  useEffect(() => {
    if (!isOpen) return;

    setPayoutDate(getDefaultPayoutDate(currentDate));

    if (companyId === undefined) {
      setPreviewState({ status: "error" });
      return;
    }

    const currentCompanyId = companyId;
    let isMounted = true;

    const loadPreview = async (): Promise<void> => {
      setPreviewState({ status: "loading" });

      try {
        const response = await Api().company.salary.getSalaryPayoutPreview({
          companyId: currentCompanyId,
          year: currentDate.year(),
          month: currentDate.monthValue(),
        });

        if (!isMounted) return;

        setPreviewState({
          status: "loaded",
          data: normalizeSalaryPayoutPreview(response.data),
        });
      } catch {
        if (!isMounted) return;
        setPreviewState({ status: "error" });
      }
    };

    void loadPreview();

    return () => {
      isMounted = false;
    };
  }, [companyId, currentDate, isOpen]);

  const payoutWorkerCount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return previewState.data.payoutWorkers.length;
  }, [previewState]);

  const excludedWorkerCount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return (
      previewState.data.noPaymentMethodWorkers.length +
      previewState.data.noPayoutWorkers.length
    );
  }, [previewState]);

  const totalNetPayoutAmount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return previewState.data.payoutWorkers.reduce((sum, worker) => {
      return sum + worker.payoutAmount;
    }, 0);
  }, [previewState]);

  const canCreatePayoutFile =
    companyId !== undefined &&
    previewState.status === "loaded" &&
    previewState.data.payoutWorkers.length > 0 &&
    payoutDate !== null &&
    !isCreatingPayoutFile;

  const closePanel = useCallback((): void => {
    if (isCreatingPayoutFileRef.current) return;
    setIsOpen(false);
  }, []);

  const openPanel = (): void => {
    setIsOpen(true);
  };

  const createPayoutFile = async (): Promise<void> => {
    if (
      isCreatingPayoutFileRef.current ||
      companyId === undefined ||
      payoutDate === null ||
      previewState.status !== "loaded"
    ) {
      return;
    }

    const workerIds = previewState.data.payoutWorkers.map((worker) => {
      return worker.workerId;
    });

    if (workerIds.length === 0) return;

    const request: CreateSalaryPayoutFileRequest = {
      payoutDate: payoutDate.toString(),
      workerIds,
    };
    const payoutYear = currentDate.year();
    const payoutMonth = currentDate.monthValue();

    isCreatingPayoutFileRef.current = true;
    setIsCreatingPayoutFile(true);

    try {
      const response = await Api().company.salary.createSalaryPayoutFile({
        companyId,
        year: payoutYear,
        month: payoutMonth,
        request,
      });

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/xml" });

      downloadBlob(blob, `Betalfil_${payoutYear}-${payoutMonth}.xml`);
      setIsOpen(false);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("Salary.PaymentFileCreatedTitle"),
          message: t("Salary.PaymentFileCreatedMessage"),
        },
      });
    } catch (error: unknown) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: resolveSalaryPayoutCreateErrorMessage(
            getBackendErrorMessageFromUnknown(error),
            t,
          ),
        },
      });
    } finally {
      isCreatingPayoutFileRef.current = false;
      setIsCreatingPayoutFile(false);
    }
  };

  return {
    canCreatePayoutFile,
    closePanel,
    createPayoutFile,
    excludedWorkerCount,
    isCreatingPayoutFile,
    isOpen,
    openPanel,
    payoutDate,
    payoutWorkerCount,
    previewState,
    setPayoutDate,
    totalNetPayoutAmount,
  };
};
